import { Loader2 } from 'lucide-react'
import type React from 'react'
import { useState } from 'react'
import AnimatedSection from './AnimatedSection'
import Container from './Container'

interface FormData {
  name: string
  whatsapp: string
  email: string
  budget: string
  objective: string
}

const FLOWESTATE_FORWARDER_URLS = [
  'https://forwarder.flowestate.app/ingest/landing-bavarian',
  'https://flowestate-lead-forwarder.luis-sifontes.workers.dev/ingest/landing-bavarian',
] as const

async function submitToFlowEstateForwarder(payload: Record<string, unknown>) {
  let lastError: Error | null = null

  for (const url of FLOWESTATE_FORWARDER_URLS) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(`Forwarder request failed with ${response.status}`)
      }

      return response
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Forwarder request failed')
    }
  }

  throw lastError ?? new Error('Forwarder request failed')
}

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    whatsapp: '',
    email: '',
    budget: '',
    objective: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const payload = {
        full_name: formData.name,
        email: formData.email,
        phone: formData.whatsapp,
        budget: formData.budget,
        objective: formData.objective,
        source: 'landing-bavarian',
        timestamp: new Date().toISOString(),
        landing_page_url: window.location.href,
      }

      await submitToFlowEstateForwarder(payload)

      window.location.href = '/thank-you'
    } catch (error) {
      console.error('Error submitting form:', error)
      alert('No pudimos enviar tu solicitud en este momento. Intenta nuevamente en unos minutos.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="contacto" className="bg-surface py-24 md:py-32">
      <Container size="lg">
        <AnimatedSection className="mx-auto mb-12 max-w-3xl text-center">
          <span className="section-label mb-4 block">Contacto</span>
          <h2 className="accent-line-center mb-6 text-3xl font-bold text-primary md:text-4xl lg:text-5xl">
            Agenda tu llamada y recibe disponibilidad
          </h2>
          <p className="text-lg text-text-secondary">
            Completa el formulario o agenda directamente. Te enviaremos opciones alineadas a tu
            presupuesto.
          </p>
        </AnimatedSection>

        <div className="mx-auto max-w-3xl">
          <AnimatedSection>
            <div className="rounded-2xl border border-border-light bg-white p-6 shadow-sm md:p-8">
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label htmlFor="name" className="mb-2 block text-sm font-medium text-primary">
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full rounded-lg border border-border-light px-4 py-3 transition-all duration-200 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent"
                      placeholder="Tu nombre"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="whatsapp"
                      className="mb-2 block text-sm font-medium text-primary"
                    >
                      WhatsApp *
                    </label>
                    <input
                      type="tel"
                      id="whatsapp"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleInputChange}
                      required
                      className="w-full rounded-lg border border-border-light px-4 py-3 transition-all duration-200 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent"
                      placeholder="+1 809 123 4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-primary">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full rounded-lg border border-border-light px-4 py-3 transition-all duration-200 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent"
                      placeholder="tu@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="budget" className="mb-2 block text-sm font-medium text-primary">
                      Presupuesto *
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      required
                      className="w-full rounded-lg border border-border-light bg-white px-4 py-3 transition-all duration-200 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent"
                    >
                      <option value="">Selecciona</option>
                      <option value="menos-150k">Menos de 150K</option>
                      <option value="150k-250k">150K – 250K</option>
                      <option value="250k-400k">250K – 400K</option>
                      <option value="400k+">400K+</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="objective"
                      className="mb-2 block text-sm font-medium text-primary"
                    >
                      Objetivo *
                    </label>
                    <select
                      id="objective"
                      name="objective"
                      value={formData.objective}
                      onChange={handleInputChange}
                      required
                      className="w-full rounded-lg border border-border-light bg-white px-4 py-3 transition-all duration-200 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent"
                    >
                      <option value="">Selecciona</option>
                      <option value="inversion">Inversión</option>
                      <option value="renta">Renta vacacional</option>
                      <option value="personal">Uso personal</option>
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-accent px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-accent-dark disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden={true} />
                      Enviando...
                    </>
                  ) : (
                    'Solicitar información'
                  )}
                </button>
              </form>
            </div>
          </AnimatedSection>
        </div>
      </Container>
    </section>
  )
}

export default ContactSection
